import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import {
  FaArrowRight,
  FaCheckCircle,
  FaGlobe,
  FaHandshake,
  FaPause,
  FaPlane,
  FaPlay,
  FaRocket,
  FaUsers,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Add_Client_Action } from "../Redux/Actions/ClientAction";
import { useLanguage } from "./LanguageProvider";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import "./HeroSection.css";

gsap.registerPlugin(ScrollTrigger);

export const HeroSection = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const [displayedTitle, setDisplayedTitle] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [spotlightPos, setSpotlightPos] = useState({ x: -999, y: -999 });
  const [cockpit, setCockpit] = useState({
    alt: "35,000",
    spd: "520",
    hdg: "247",
  });

  /* ── refs ── */
  const heroRef = useRef(null);
  const heroSectionRef = useRef(null);
  const contentRef = useRef(null);
  const brandNameRef = useRef(null);
  const titleVisibleRef = useRef(null);
  const descVisibleRef = useRef(null);
  const sectionDividerRef = useRef(null);
  const phoneSectionRef = useRef(null);
  const videoWrapperRef = useRef(null);
  const swiperRef = useRef(null);
  const discoverBtnRef = useRef(null);
  const discoverBtnRef2 = useRef(null);
  const aboutVisualRef = useRef(null);
  const aboutInfoRef = useRef(null);
  const featureItemsRef = useRef([]);
  const ghostTextRef = useRef(null);
  const planeRef = useRef(null);

  const { currentLang } = useLanguage();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isRTL = () => currentLang === "ar";

  /* ════════════════════════════════════════
     TRANSLATIONS
     ════════════════════════════════════════ */
  const translations = {
    EN: {
      discoverBtn: "EXPLORE SERVICES",
      phoneTitle: "Request a Private Consultation",
      phoneSubtitle:
        "Leave your number and a FlyVia specialist will reach out to tailor the perfect aviation or residency solution for you.",
      phonePlaceholder: "Enter your phone number",
      submitBtn: "Submit",
      submittedBtn: "✓ Submitted",
      successMessage: "Thank you! Our team will contact you soon.",
      aboutTitle: "About FlyVia",
      aboutSubtitle:
        "FlyVia helps individuals, families, freelancers, and business owners move to Spain and Europe through clear guidance, practical support, and organized execution.",
      missionTitle: "Our Mission",
      missionDesc:
        "To make relocation to Spain and Europe easier, clearer, and more practical through trusted guidance and real execution support.",
      visionTitle: "Our Vision",
      visionDesc:
        "To become a trusted partner for people and businesses looking to build a stable future in Spain and across Europe.",
      partnershipTitle: "Why FlyVia",
      partnershipDesc:
        "We do not just provide information. We help you choose the right path, prepare your case, and move forward step by step.",
      readMore: "Read more",
      pauseSlider: "Pause slider",
      playSlider: "Play slider",
      aboutExpertTitle: "Expert Guidance for Your",
      aboutExpertSpan: "European Future",
      relocations: "Relocations",
      successRate: "Success Rate",
      established: "Est.",
      relocationAlt: "Successful Relocation",
    },
    AR: {
      discoverBtn: "اكتشف الخدمات",
      phoneTitle: "احجز استشارتك الخاصة",
      phoneSubtitle:
        "اترك رقمك وسيتواصل معك متخصص FlyVia لتصميم حل الطيران أو الإقامة المثالي لك.",
      phonePlaceholder: "أدخل رقم هاتفك",
      submitBtn: "إرسال",
      submittedBtn: "✓ تم الإرسال",
      successMessage: "شكرًا لك! سيتواصل معك فريقنا قريبًا.",
      aboutTitle: "عن FlyVia",
      aboutSubtitle:
        "FlyVia تساعد الأفراد والعائلات والفريلانسرز وأصحاب الأعمال على الانتقال إلى إسبانيا وأوروبا من خلال توجيه واضح، ودعم عملي، وتنفيذ منظم.",
      missionTitle: "مهمتنا",
      missionDesc:
        "تسهيل الانتقال إلى إسبانيا وأوروبا بطريقة أوضح وأكثر عملية من خلال دعم موثوق وخطوات تنفيذ حقيقية.",
      visionTitle: "رؤيتنا",
      visionDesc:
        "أن نكون الشريك الموثوق للأفراد والأعمال الذين يريدون بناء مستقبل مستقر في إسبانيا وأوروبا.",
      partnershipTitle: "لماذا FlyVia",
      partnershipDesc:
        "نحن لا نقدم معلومات فقط، بل نساعدك على اختيار الطريق الصحيح، وتجهيز ملفك، والتحرك خطوة بخطوة بثقة.",
      readMore: "اقرأ المزيد",
      pauseSlider: "إيقاف السلايدر",
      playSlider: "تشغيل السلايدر",
      aboutExpertTitle: "توجيهات احترافية لمستقبلك",
      aboutExpertSpan: "في إسبانيا وأوروبا",
      relocations: "عملية انتقال",
      successRate: "نسبة النجاح",
      established: "تأسست",
      relocationAlt: "انتقال ناجح",
    },
  };

  const t = useCallback(
    (key) => {
      const k = currentLang.toUpperCase();
      return translations[k]?.[key] || translations.EN[key] || key;
    },
    [currentLang],
  );

  /* ════════════════════════════════════════
     SLIDES DATA
     ════════════════════════════════════════ */
  const heroSlides = useMemo(() => {
    const data = {
      EN: [
        {
          id: 1,
          shortTitle: "Private Jet",
          title: "Private Jet Charter",
          kicker: "Bespoke Luxury Aviation",
          description:
            "Fly on your terms. FlyVia offers white-glove private jet charters — from last-minute escapes to globe-spanning executive journeys — with zero compromise on comfort, privacy, or speed.",
          mediaType: "image",
          media: "/3734647c-967f-4bbd-8564-7d8502d99581.png",
          alt: "Luxury private jet experience",
        },
        {
          id: 2,
          shortTitle: "Empty Leg",
          title: "Empty Leg Flights",
          kicker: "Elite Travel, Smarter Price",
          description:
            "Claim a private jet repositioning flight and experience first-class luxury at a fraction of the charter cost. Exclusive routes, no crowds, no compromises.",
          mediaType: "image",
          media: "/3ec4a634-7947-4c0f-8aec-d83fdc9a5545.png",
          alt: "Empty leg flight",
        },
        {
          id: 3,
          shortTitle: "Sightseeing",
          title: "Aerial Sightseeing",
          kicker: "The Sky Is Your Canvas",
          description:
            "Drift above the world's most breathtaking landscapes in total serenity. Our bespoke aerial tours transform sightseeing into a luxury private flying experience.",
          mediaType: "image",
          media: "/786023d6-fbb8-4bab-96e4-bacbaa41b0bb.png",
          alt: "Flight sightseeing view",
        },
        {
          id: 4,
          shortTitle: "Helicopter",
          title: "Helicopter Transport",
          kicker: "Above the Ordinary",
          description:
            "Bypass ground traffic entirely. Our premium helicopter transfers connect airports, city centres, and private estates with unmatched speed and executive-level discretion.",
          mediaType: "image",
          media: "/c22ffc02-5056-42e8-a324-483266c061b5.png",
          alt: "Helicopter transport",
        },
        {
          id: 5,
          shortTitle: "Aerial Ads",
          title: "Aerial Advertising",
          kicker: "Campaigns That Soar",
          description:
            "Command attention from every angle. FlyVia aerial advertising elevates your brand above the competition — literally — with high-impact sky campaigns over crowds and coastlines.",
          mediaType: "image",
          media: "/67bae2fe-43f7-4640-8848-2e038b92b118.png",
          alt: "Aerial advertising in the sky",
        },
        {
          id: 6,
          shortTitle: "Paramotor",
          title: "Paramotor Campaigns",
          kicker: "Bold. Airborne. Unforgettable.",
          description:
            "Turn heads at festivals, sporting events, and open-air venues with spectacular low-altitude paramotor campaigns that put your brand in the sky where no billboard can reach.",
          mediaType: "image",
          media: "/4561807d-d9ae-4e40-b1ea-b7d8f692f6ea.png",
          alt: "Paramotor campaign",
        },
        {
          id: 7,
          shortTitle: "Residency",
          title: "Spain Residency",
          kicker: "Your Gateway to Europe",
          description:
            "Unlock a life in Spain. FlyVia guides you through every residency route — Golden Visa, Digital Nomad, Non-Lucrative — with expert legal coordination and hands-on support from day one.",
          mediaType: "image",
          media: "/7808dc2b-5323-40ba-ae6a-cc06f7f4c44d.png",
          alt: "Barcelona city skyline in Spain",
        },
        {
          id: 8,
          shortTitle: "Corporate",
          title: "Corporate Setup",
          kicker: "Your European Headquarters",
          description:
            "Establish your business presence in Europe with confidence. FlyVia handles company formation, tax structure, documentation, and compliance so you can focus on building your empire.",
          mediaType: "image",
          media: "/1da517ba-2435-4970-9121-3083c5f5b6d4.png",
          alt: "Business people shaking hands in a meeting",
        },
        {
          id: 9,
          shortTitle: "Relocation",
          title: "Elite Relocation",
          kicker: "Move in Luxury, Arrive in Style",
          description:
            "Your relocation deserves the same precision as a private flight. FlyVia orchestrates every detail of your move to Europe — from housing and schooling to residency and settlement.",
          mediaType: "image",
          media: "/d891f915-9fb8-4d36-8a14-d25054aa9248.png",
          alt: "Woman walking with luggage",
        },
        {
          id: 10,
          shortTitle: "Real Estate",
          title: "Luxury Real Estate",
          kicker: "Mediterranean Prestige",
          description:
            "From sun-drenched Costa Blanca villas to Barcelona penthouses — FlyVia connects you with exclusive off-market properties in Spain's most coveted addresses.",
          mediaType: "image",
          media: "/3234a3fc-145e-47ce-a1b1-5c6bff6cc62d.png",
          alt: "Luxury villa house",
        },
        {
          id: 11,
          shortTitle: "Concierge",
          title: "VIP Concierge",
          kicker: "Every Detail, Perfected",
          description:
            "From private jet bookings and luxury transfers to restaurant reservations and lifestyle management — FlyVia's concierge team ensures every moment of your life is effortlessly curated.",
          mediaType: "image",
          media:
            "https://images.pexels.com/photos/5378703/pexels-photo-5378703.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1280&fit=crop",
          alt: "Guests at luxury hotel reception",
        },
      ],
      AR: [
        {
          id: 1,
          shortTitle: "طيران خاص",
          title: "تشارتر الطائرات الخاصة",
          kicker: "طيران فاخر لا مثيل له",
          description:
            "اطّر وجهتك بشروطك أنت. FlyVia تقدم رحلات طيران خاص بمعاير راقية — من الرحلات العاجلة إلى الجولات التنفيذية حول العالم — دون أي تنازل عن الراحة أو الخصوصية.",
          mediaType: "image",
          media:
            "https://swiftjetaviation.com/wp-content/uploads/2024/04/pngwing.com-5.png",
          alt: "Luxury private jet experience",
        },
        {
          id: 2,
          shortTitle: "إمبتي ليج",
          title: "رحلات إمبتي ليج",
          kicker: "فخامة الطيران الخاص بسعر استثنائي",
          description:
            "احجز رحلة Empty Leg واستمتع بتجربة الطيران الخاص الكاملة — كرسي واسع، كابين خاص، لا ازدحام — بجزء من تكلفة التشارتر العادي.",
          mediaType: "image",
          media:
            "https://swiftjetaviation.com/wp-content/uploads/2024/03/steptodown.com127589.jpg",
          alt: "Empty leg flight",
        },
        {
          id: 3,
          shortTitle: "جولات جوية",
          title: "جولات المشاهدة الجوية",
          kicker: "السماء لوحة فنية خاصة بك",
          description:
            "حلّق فوق أجمل المناظر الطبيعية في أجواء من الهدوء التام. جولاتنا الجوية المخصصة تحوّل المشاهدة إلى تجربة طيران خاص لا تُنسى.",
          mediaType: "video",
          media: "/flight.mp4",
          alt: "Flight sightseeing view",
        },
        {
          id: 4,
          shortTitle: "مروحيات",
          title: "النقل المروحي",
          kicker: "فوق الازدحام — بأناقة",
          description:
            "تخطَّ الازدحام كلياً. تحويلاتنا المروحية الفاخرة تربط المطارات والمدن والإقامات الخاصة بسرعة لا مثيل لها وخصوصية تنفيذية مطلقة.",
          mediaType: "image",
          media: "/c22ffc02-5056-42e8-a324-483266c061b5.png",
          alt: "Helicopter transport",
        },
        {
          id: 5,
          shortTitle: "إعلانات جوية",
          title: "الإعلانات الجوية",
          kicker: "حملاتك تحلّق في السماء",
          description:
            "أبهر جمهورك من كل الزوايا. إعلانات FlyVia الجوية ترفع علامتك التجارية فوق المنافسين — حرفياً — بحملات عالية التأثير فوق الحشود والشواطئ.",
          mediaType: "image",
          media:
            "https://swiftjetaviation.com/wp-content/uploads/2024/03/arial-min-1024x617.png",
          alt: "Aerial advertising in the sky",
        },
        {
          id: 6,
          shortTitle: "باراموتور",
          title: "حملات الباراموتور",
          kicker: "جريء. جوي. لا يُنسى.",
          description:
            "استقطب الأنظار في المهرجانات والفعاليات الرياضية والأماكن المفتوحة بحملات باراموتور مذهلة تضع علامتك في السماء حيث لا تصل أي لافتة.",
          mediaType: "image",
          media: "/paramotor/paramotor-1.png",
          alt: "Paramotor campaign",
        },
        {
          id: 7,
          shortTitle: "الإقامة",
          title: "الإقامة في إسبانيا",
          kicker: "بوابتك إلى أوروبا",
          description:
            "افتح أبواب الحياة في إسبانيا. FlyVia ترشدك في كل مسارات الإقامة — الفيزا الذهبية، الرحّال الرقمي، غير الرابح — بتنسيق قانوني احترافي ودعم يومي من اليوم الأول.",
          mediaType: "image",
          media:
            "https://images.pexels.com/photos/16058930/pexels-photo-16058930.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1280&fit=crop",
          alt: "Barcelona city skyline in Spain",
        },
        {
          id: 8,
          shortTitle: "الشركات",
          title: "تأسيس الشركات",
          kicker: "مقرك الأوروبي ينتظرك",
          description:
            "أسّس حضورك التجاري في أوروبا بثقة. FlyVia تتولى تأسيس الشركة، الهيكل الضريبي، المستندات، والامتثال القانوني — لتركّز أنت على بناء إمبراطوريتك.",
          mediaType: "image",
          media:
            "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1280&fit=crop",
          alt: "Business people shaking hands in a meeting",
        },
        {
          id: 9,
          shortTitle: "الانتقال",
          title: "الانتقال الراقي",
          kicker: "انتقل بفخامة — استقر بأناقة",
          description:
            "انتقالك يستحق نفس الدقة التي تحظى بها رحلة الطيرة الخاص. FlyVia تُنسّق كل تفاصيل انتقالك إلى أوروبا — من السكن والتعليم إلى الإقامة والاستقرار.",
          mediaType: "image",
          media:
            "https://images.pexels.com/photos/1008155/pexels-photo-1008155.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1280&fit=crop",
          alt: "Woman walking with luggage",
        },
        {
          id: 10,
          shortTitle: "العقارات",
          title: "العقارات الفاخرة",
          kicker: "مكانة البحر الأبيض المتوسط",
          description:
            "من فيلات كوستا بلانكا المشمسة إلى بنتهاوس برشلونة — FlyVia تفتح لك أبواب العقارات الحصرية غير المدرجة في أرقى عناوين إسبانيا.",
          mediaType: "image",
          media:
            "https://images.pexels.com/photos/16573669/pexels-photo-16573669.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1280&fit=crop",
          alt: "Luxury villa house",
        },
        {
          id: 11,
          shortTitle: "كونسيرج",
          title: "VIP Concierge",
          kicker: "كل تفصيلة — في أكمل صورة",
          description:
            "من حجوزات الطيرة الخاص والتنقلات الفاخرة إلى حجوزات المطاعم وإدارة نمط الحياة — فريق كونسيرج FlyVia يضمن أن كل لحظة في حياتك تُدار باحترافية لا مثيل لها.",
          mediaType: "image",
          media:
            "https://images.pexels.com/photos/5378703/pexels-photo-5378703.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1280&fit=crop",
          alt: "Guests at luxury hotel reception",
        },
      ],
    };
    const k = currentLang.toUpperCase();
    return data[k] || data.EN;
  }, [currentLang]);

  const activeHeroSlide = heroSlides[activeSlide] || heroSlides[0];

  /* أطول عنوان ووصف لحجز المساحة */
  const longestTitle = useMemo(
    () =>
      heroSlides.reduce(
        (a, s) => (s.title.length > a.length ? s.title : a),
        "",
      ),
    [heroSlides],
  );
  const longestDescription = useMemo(
    () =>
      heroSlides.reduce(
        (a, s) => (s.description.length > a.length ? s.description : a),
        "",
      ),
    [heroSlides],
  );

  /* ════════════════════════════════════════
     ABOUT DATA
     ════════════════════════════════════════ */
  const aboutCardsData = useMemo(
    () => [
      {
        id: 1,
        icon: <FaRocket />,
        title: t("missionTitle"),
        description: t("missionDesc"),
        color: "#ae0001",
      },
      {
        id: 2,
        icon: <FaGlobe />,
        title: t("visionTitle"),
        description: t("visionDesc"),
        color: "#2c3b5b",
      },
      {
        id: 3,
        icon: <FaHandshake />,
        title: t("partnershipTitle"),
        description: t("partnershipDesc"),
        color: "#ae0001",
      },
    ],
    [t],
  );

  const statsData = [
    { label: t("relocations"), value: "500+", icon: <FaUsers /> },
    { label: t("successRate"), value: "98%", icon: <FaCheckCircle /> },
  ];

  /* ════════════════════════════════════════
     HANDLERS
     ════════════════════════════════════════ */
  const goToAboutUsPage = useCallback(
    () => navigate("/Aboutuspage"),
    [navigate],
  );
  const scrollToServices = useCallback(
    () => navigate("/Ourservicepage"),
    [navigate],
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!phoneNumber.trim()) return;
      await dispatch(
        Add_Client_Action({
          name: "No Name",
          email: "No Email",
          phone: phoneNumber,
          whatsappNumber: phoneNumber,
          jobTitle: "No Job Title",
          message: "Register From Herosection",
          countryName: "No Country",
        }),
      );
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setPhoneNumber("");
      }, 3000);
    },
    [phoneNumber, dispatch],
  );

  const toggleVideoPlay = useCallback(() => {
    const sw = swiperRef.current;
    if (!sw?.autoplay) return;
    isPlaying ? sw.autoplay.stop() : sw.autoplay.start();
    setIsPlaying((p) => !p);
  }, [isPlaying]);

  const goToSlide = useCallback((index) => {
    swiperRef.current?.slideToLoop(index);
  }, []);

  /* ════════════════════════════════════════
     TYPEWRITER
     ════════════════════════════════════════ */
  useEffect(() => {
    const title = heroSlides[activeSlide]?.title || "";
    setDisplayedTitle("");
    setIsTyping(true);

    let i = 0;
    const id = setInterval(() => {
      i++;
      setDisplayedTitle(title.slice(0, i));
      if (i >= title.length) {
        clearInterval(id);
        setIsTyping(false);
      }
    }, 38);

    return () => clearInterval(id);
  }, [activeSlide, heroSlides]);

  /* ════════════════════════════════════════
     COCKPIT
     ════════════════════════════════════════ */
  useEffect(() => {
    const id = setInterval(() => {
      setCockpit({
        alt: (34800 + Math.floor(Math.random() * 400)).toLocaleString(),
        spd: String(515 + Math.floor(Math.random() * 12)),
        hdg: String(244 + Math.floor(Math.random() * 7)).padStart(3, "0"),
      });
    }, 2200);
    return () => clearInterval(id);
  }, []);

  /* ════════════════════════════════════════
     MOUSE SPOTLIGHT
     ════════════════════════════════════════ */
  useEffect(() => {
    const section = heroSectionRef.current;
    if (!section) return;
    const onMove = (e) => {
      const r = section.getBoundingClientRect();
      setSpotlightPos({ x: e.clientX - r.left, y: e.clientY - r.top });
    };
    const onLeave = () => setSpotlightPos({ x: -999, y: -999 });
    section.addEventListener("mousemove", onMove);
    section.addEventListener("mouseleave", onLeave);
    return () => {
      section.removeEventListener("mousemove", onMove);
      section.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  /* ════════════════════════════════════════
     PARALLAX
     ════════════════════════════════════════ */
  useEffect(() => {
    const section = heroSectionRef.current;
    const content = contentRef.current;
    if (!section || !content) return;
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 18;
      const y = (e.clientY / window.innerHeight - 0.5) * 10;
      content.style.transform = `translate(${x}px,${y}px)`;
    };
    const onLeave = () => {
      content.style.transform = "translate(0,0)";
    };
    section.addEventListener("mousemove", onMove);
    section.addEventListener("mouseleave", onLeave);
    return () => {
      section.removeEventListener("mousemove", onMove);
      section.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  /* ════════════════════════════════════════
     MAGNETIC BUTTON
     ════════════════════════════════════════ */
  useEffect(() => {
    const btn = discoverBtnRef2.current;
    if (!btn) return;
    const onMove = (e) => {
      const r = btn.getBoundingClientRect();
      btn.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * 0.35}px,${(e.clientY - r.top - r.height / 2) * 0.35}px)`;
    };
    const onLeave = () => {
      btn.style.transform = "translate(0,0)";
    };
    btn.addEventListener("mousemove", onMove);
    btn.addEventListener("mouseleave", onLeave);
    return () => {
      btn.removeEventListener("mousemove", onMove);
      btn.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  /* ════════════════════════════════════════
     GSAP
     ════════════════════════════════════════ */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(brandNameRef.current, {
        y: -30,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
      });
      gsap.from(titleVisibleRef.current, {
        y: 50,
        opacity: 0,
        duration: 1,
        delay: 0.2,
        ease: "power3.out",
      });
      gsap.from(descVisibleRef.current, {
        y: 30,
        opacity: 0,
        duration: 1,
        delay: 0.4,
        ease: "power3.out",
      });

      if (ghostTextRef.current) {
        gsap.to(ghostTextRef.current, {
          y: -100,
          scrollTrigger: {
            trigger: ghostTextRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      }

      const av = aboutVisualRef.current;
      if (av) {
        const onMM = (e) => {
          const { left, top, width, height } = av.getBoundingClientRect();
          gsap.to(av, {
            rotationY: (e.clientX - left - width / 2) / 20,
            rotationX: -(e.clientY - top - height / 2) / 20,
            transformPerspective: 1000,
            duration: 0.5,
            ease: "power2.out",
          });
        };
        const onML = () =>
          gsap.to(av, {
            rotationY: 0,
            rotationX: 0,
            duration: 0.8,
            ease: "power3.out",
          });
        window.addEventListener("mousemove", onMM);
        av.addEventListener("mouseleave", onML);

        if (planeRef.current) {
          gsap.to(planeRef.current, {
            x: "100vw",
            y: -100,
            rotation: 20,
            duration: 20,
            repeat: -1,
            ease: "none",
            scrollTrigger: {
              trigger: ".premium-about-section",
              start: "top bottom",
              toggleActions: "play none none none",
            },
          });
        }
      }
    }, heroRef);
    return () => ctx.revert();
  }, [currentLang]);

  useEffect(() => {
    if (!descVisibleRef.current) return;
    gsap.fromTo(
      descVisibleRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.5, ease: "power2.out" },
    );
  }, [activeSlide]);

  /* ════════════════════════════════════════
     RENDER
     ════════════════════════════════════════ */
  return (
    <div className="hero-container" ref={heroRef} dir={isRTL() ? "rtl" : "ltr"}>
      {/* ══════════════════════════════
          HERO SECTION
          ══════════════════════════════ */}
      <section className="hero-section" id="home" ref={heroSectionRef}>
        {/* spotlight */}
        <div
          className="hero-spotlight"
          style={{
            left: spotlightPos.x,
            top: spotlightPos.y,
            opacity: spotlightPos.x === -999 ? 0 : 1,
          }}
        />

        {/* decorative layers */}
        <div className="hero-noise" aria-hidden="true" />
        <div className="hero-dot-grid" aria-hidden="true" />
        <div className="hero-scan-line" />
        <div
          className="hero-scan-line"
          style={{ animationDelay: "-4s", opacity: 0.4 }}
        />

        {/* plane flyby */}
        <div className="hero-plane-flyby" aria-hidden="true">
          <FaPlane />
        </div>

        {/* cockpit */}
        <div className="hero-cockpit-panel" aria-hidden="true">
          <div className="hero-cockpit-item">
            <span className="hero-cockpit-label">ALT ft</span>
            <span className="hero-cockpit-value">{cockpit.alt}</span>
          </div>
          <div className="hero-cockpit-item">
            <span className="hero-cockpit-label">SPD kts</span>
            <span className="hero-cockpit-value">{cockpit.spd}</span>
          </div>
          <div className="hero-cockpit-item">
            <span className="hero-cockpit-label">HDG °</span>
            <span className="hero-cockpit-value">{cockpit.hdg}</span>
          </div>
        </div>

        {/* est badge */}
        <div className="hero-est-badge" aria-hidden="true">
          <span className="hero-est-year">2024</span>
          <span className="hero-est-label">Est.</span>
        </div>

        {/* aurora */}
        <div className="hero-aurora" aria-hidden="true">
          <div
            className="hero-aurora-blob"
            style={{
              width: "600px",
              height: "600px",
              top: "-20%",
              left: "-10%",
              background: "rgba(174,0,1,0.08)",
              animationDuration: "24s",
            }}
          />
          <div
            className="hero-aurora-blob"
            style={{
              width: "400px",
              height: "400px",
              top: "30%",
              right: "-5%",
              background: "rgba(174,0,1,0.06)",
              animationDuration: "30s",
              animationDelay: "-6s",
            }}
          />
          <div
            className="hero-aurora-blob"
            style={{
              width: "300px",
              height: "300px",
              bottom: "10%",
              left: "30%",
              background: "rgba(100,0,1,0.05)",
              animationDuration: "20s",
              animationDelay: "-3s",
            }}
          />
        </div>

        {/* particles */}
        {[
          { top: "15%", left: "8%", dur: "14s", delay: "0s" },
          { top: "25%", left: "15%", dur: "18s", delay: "-3s" },
          { top: "60%", left: "6%", dur: "16s", delay: "-5s" },
          { top: "70%", left: "20%", dur: "20s", delay: "-1s" },
          { top: "20%", right: "8%", dur: "17s", delay: "-2s" },
          { top: "50%", right: "12%", dur: "13s", delay: "-4s" },
          { top: "75%", right: "20%", dur: "21s", delay: "-7s" },
          { top: "40%", left: "50%", dur: "25s", delay: "-9s" },
          { top: "85%", left: "40%", dur: "16s", delay: "-2s" },
          { top: "10%", left: "55%", dur: "19s", delay: "-6s" },
        ].map((p, i) => (
          <div
            key={i}
            className="hero-particle"
            aria-hidden="true"
            style={{
              top: p.top,
              left: p.left,
              right: p.right,
              animationDuration: p.dur,
              animationDelay: p.delay,
              width: i % 3 === 0 ? "4px" : "2px",
              height: i % 3 === 0 ? "4px" : "2px",
            }}
          />
        ))}

        {/* corners & lines */}
        <div className="hero-corner hero-corner-tl" aria-hidden="true" />
        <div className="hero-corner hero-corner-tr" aria-hidden="true" />
        <div className="hero-corner hero-corner-bl" aria-hidden="true" />
        <div className="hero-corner hero-corner-br" aria-hidden="true" />
        <div
          className="hero-side-line hero-side-line-left"
          style={{ height: "80px" }}
          aria-hidden="true"
        />
        <div
          className="hero-side-line hero-side-line-right"
          style={{ height: "80px" }}
          aria-hidden="true"
        />

        {/* slide counter */}
        <div className="hero-slide-counter" aria-hidden="true">
          <span className="hero-counter-current">
            {String(activeSlide + 1).padStart(2, "0")}
          </span>
          <div className="hero-counter-sep" />
          <span className="hero-counter-total">
            {String(heroSlides.length).padStart(2, "0")}
          </span>
        </div>

        {/* progress bar */}
        <div className="hero-progress-bar-wrap" aria-hidden="true">
          <div key={activeSlide} className="hero-progress-bar" />
        </div>

        {/* background swiper */}
        <div className="hero-background-container">
          <div className="hero-background-curve">
            <div className="video-wrapper" ref={videoWrapperRef}>
              <Swiper
                modules={[Autoplay, EffectFade, Pagination]}
                effect="fade"
                loop
                speed={1200}
                autoplay={{ delay: 4200, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                onSwiper={(sw) => {
                  swiperRef.current = sw;
                }}
                onSlideChange={(sw) => setActiveSlide(sw.realIndex)}
                className="hero-media-swiper"
              >
                {heroSlides.map((slide) => (
                  <SwiperSlide key={slide.id}>
                    <div className="hero-media-slide">
                      {slide.mediaType === "video" ? (
                        <video
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="hero-background-video"
                        >
                          <source src={slide.media} type="video/mp4" />
                        </video>
                      ) : (
                        <img
                          src={slide.media}
                          alt={slide.alt}
                          className="hero-background-image"
                          loading={slide.id === 1 ? "eager" : "lazy"}
                        />
                      )}
                      <div className="video-overlay" />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <button
                className="video-control-btn"
                onClick={toggleVideoPlay}
                aria-label={isPlaying ? t("pauseSlider") : t("playSlider")}
              >
                {isPlaying ? <FaPause /> : <FaPlay />}
              </button>
            </div>
          </div>
        </div>

        {/* HERO CONTENT */}
        <div className="hero-content">
          <Container>
            <Row className="justify-content-center">
              <Col
                lg={10}
                className="text-center"
                ref={contentRef}
                style={{ transition: "transform 0.15s ease-out" }}
              >
                {/* brand */}
                <div className="brand-name" ref={brandNameRef}>
                  <span className="softy">FLY</span>
                  <span className="pinko">VIA</span>
                </div>

                {/* kicker */}
                <div
                  key={`kicker-${activeSlide}`}
                  className="hero-kicker-badge mx-auto"
                >
                  <div className="hero-kicker-dot" />
                  {activeHeroSlide.kicker}
                </div>

                {/* ══════════════════════════════════════════
                    TITLE — الـ cursor inline جنب النص
                    ══════════════════════════════════════════ */}
                <div className="main-title-wrapper">
                  {/* placeholder (يحجز مساحة سطر واحد فقط) */}
                  <h1 className="main-title-placeholder" aria-hidden="true">
                    {longestTitle}
                  </h1>

                  {/* العنوان المرئي */}
                  <h1 className="main-title-visible" ref={titleVisibleRef}>
                    <span>{displayedTitle}</span>
                    <span className="hero-cursor" aria-hidden="true">
                      |
                    </span>
                  </h1>
                </div>

                {/* underline */}
                <span
                  className="hero-title-underline"
                  key={activeSlide}
                  aria-hidden="true"
                />

                {/* ══════════════════════════════════════════
                    DESCRIPTION
                    ══════════════════════════════════════════ */}
                <div className="description-wrapper">
                  <p className="description-placeholder" aria-hidden="true">
                    {longestDescription}
                  </p>
                  <p className="description-visible" ref={descVisibleRef}>
                    {activeHeroSlide.description}
                  </p>
                </div>

                {/* service chips */}
                <div className="hero-services-strip">
                  {heroSlides.map((slide, index) => (
                    <button
                      key={slide.id}
                      type="button"
                      className={`hero-service-chip ${index === activeSlide ? "active" : ""}`}
                      onClick={() => goToSlide(index)}
                    >
                      {slide.shortTitle}
                    </button>
                  ))}
                </div>

                {/* CTA */}
                <div className="d-flex justify-content-center">
                  <Button
                    className="discover-btn hover:!text-white"
                    onClick={scrollToServices}
                    ref={(el) => {
                      discoverBtnRef.current = el;
                      discoverBtnRef2.current = el;
                    }}
                  >
                    {t("discoverBtn")}
                  </Button>
                </div>

                {/* divider */}
                <div className="section-divider" ref={sectionDividerRef} />

                {/* phone */}
                <div className="phone-section" ref={phoneSectionRef}>
                  <h3 className="phone-title">{t("phoneTitle")}</h3>
                  <p className="phone-subtitle">{t("phoneSubtitle")}</p>

                  <Form onSubmit={handleSubmit} className="phone-form">
                    <div className="input-group-wrapper">
                      <div className="phone-input-group">
                        <Form.Control
                          type="tel"
                          className="phone-input"
                          placeholder={t("phonePlaceholder")}
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          required
                          aria-label={t("phonePlaceholder")}
                        />
                      </div>
                      <Button
                        type="submit"
                        className={`submit-btn ${isSubmitted ? "success" : ""}`}
                        disabled={!phoneNumber.trim()}
                      >
                        {isSubmitted ? t("submittedBtn") : t("submitBtn")}
                      </Button>
                    </div>
                    {isSubmitted && (
                      <div className="success-message" role="status">
                        {t("successMessage")}
                      </div>
                    )}
                  </Form>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      {/* ══════════════════════════════
          ABOUT SECTION
          ══════════════════════════════ */}
      <section className="premium-about-section" id="about">
        <div className="about-ghost-text" ref={ghostTextRef} aria-hidden="true">
          FLYVIA
        </div>
        <div className="about-bg-blob" aria-hidden="true" />
        <div className="bg-plane" ref={planeRef} aria-hidden="true">
          <FaPlane />
        </div>

        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <div className="about-visual-stack" ref={aboutVisualRef}>
                {/* Visual Main Box */}
                <div className="visual-main-box">
                  <img
                    src="/about_success.png"
                    alt={t("relocationAlt")}
                    className="visual-image"
                    width="1024"
                    height="1024"
                    loading="lazy"
                    decoding="async"
                    sizes="(max-width: 480px) 280px,
           (max-width: 767px) 300px,
           (max-width: 991px) 320px,
           340px"
                  />

                  <div className="visual-image-overlay" />
                  <div className="visual-inner-glow" />

                  <div className="visual-content">
                    <div className="visual-experience-badge">
                      <span className="years">{t("established")}</span>
                      <span className="text">2024</span>
                    </div>
                  </div>
                </div>
                {statsData.map((stat, i) => (
                  <div key={i} className={`floating-stat-badge stat-${i}`}>
                    <div className="stat-icon">{stat.icon}</div>
                    <div className="stat-info">
                      <span className="stat-value">{stat.value}</span>
                      <span className="stat-label">{stat.label}</span>
                    </div>
                  </div>
                ))}
                <div className="visual-decorative-circle" aria-hidden="true" />
              </div>
            </Col>

            <Col lg={6}>
              <div className="about-info-content" ref={aboutInfoRef}>
                <h6 className="about-label">{t("aboutTitle")}</h6>
                <h2 className="about-main-title">
                  {t("aboutExpertTitle")} <span>{t("aboutExpertSpan")}</span>
                </h2>
                <p className="about-description">{t("aboutSubtitle")}</p>

                <div className="about-features-list">
                  {aboutCardsData.map((feature, i) => (
                    <div
                      key={feature.id}
                      className="about-feature-item"
                      ref={(el) => (featureItemsRef.current[i] = el)}
                    >
                      <div
                        className="feature-icon-wrapper"
                        style={{ color: feature.color }}
                      >
                        {feature.icon}
                      </div>
                      <div className="feature-text">
                        <h4>{feature.title}</h4>
                        <p>{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="about-actions">
                  <button
                    className="premium-about-btn !mx-auto lg:me-auto"
                    onClick={goToAboutUsPage}
                  >
                    <span>{t("readMore")}</span>
                    <FaArrowRight />
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};
